import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../services/supabaseClient';

const ProfilePage: React.FC = () => {
  const { profile, profileError, updateProfile } = useAuth();
  const [formData, setFormData] = useState({
    username: '',
    bio: '',
    website: '',
    twitter: '',
    linkedin: '',
    is_writer: false,
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);

  useEffect(() => {
    if (profile) {
      setFormData({
        username: profile.username || '',
        bio: profile.bio || '',
        website: profile.website || '',
        twitter: profile.twitter || '',
        linkedin: profile.linkedin || '',
        is_writer: profile.is_writer || false,
      });
    }
  }, [profile]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAvatarFile(file);
      setAvatarPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    let avatarUrl = profile?.avatar_url || undefined;

    if (avatarFile && profile?.id) {
      const fileExt = avatarFile.name.split('.').pop();
      const filePath = `public/${profile.id}_${Date.now()}.${fileExt}`;
      const { error: uploadError } = await supabase.storage.from('article_images').upload(filePath, avatarFile, { upsert: true });
      if (uploadError) {
        setError('Failed to upload avatar: ' + uploadError.message);
        setLoading(false);
        return;
      }
      const { data } = supabase.storage.from('article_images').getPublicUrl(filePath);
      avatarUrl = data.publicUrl;
    }

    try {
      await updateProfile({ ...formData, avatar_url: avatarUrl });
      setMessage('Profile updated successfully!');
      setAvatarFile(null);
      setAvatarPreview(null);
    } catch (err: any) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="utility-page min-h-screen bg-white text-black font-roboto-mono py-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {profileError && <p role="alert" className="mb-4 text-red-700">{profileError}</p>}
        <div className="bg-white border border-gray-300   overflow-hidden">
          {/* Header */}
          <div className="bg-white border-b border-gray-300 px-6 py-4 flex flex-col items-center">
            <div className="relative w-28 h-28 mb-4">
              {avatarPreview ? (
                <img src={avatarPreview} alt="Profile preview" className="w-28 h-28 rounded-full object-cover border-4 border-brand-accent shadow" />
              ) : profile?.avatar_url ? (
                <img src={profile.avatar_url} alt="Profile" className="w-28 h-28 rounded-full object-cover border-4 border-brand-accent shadow" />
              ) : (
                <div className="w-28 h-28 rounded-full bg-gray-200 flex items-center justify-center text-4xl font-bold text-gray-400 border-4 border-brand-accent">
                  {profile?.username?.[0]?.toUpperCase() || '?'}
                </div>
              )}
              <label className="absolute bottom-0 right-0 bg-brand-accent text-white rounded-full p-2 cursor-pointer  hover:bg-brand-accent-dark transition-colors">
                <input type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} />
                <span className="text-xs">Edit</span>
              </label>
            </div>
            <h1 className="text-2xl font-bold text-black">Profile Settings</h1>
            <p className="text-gray-700 mt-1">Manage your account and writer status</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {error && (
              <div className="bg-red-100 border border-red-300 text-red-700 px-4 py-3 ">
                {error}
              </div>
            )}
            
            {message && (
              <div className="bg-green-100 border border-green-300 text-green-700 px-4 py-3 ">
                {message}
              </div>
            )}

            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                  Username
                </label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border bg-white border-gray-300  focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent"
                  placeholder="Your username"
                />
              </div>

              <div>
                <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-2">
                  Website
                </label>
                <input
                  type="url"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border bg-white border-gray-300  focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent"
                  placeholder="https://yourwebsite.com"
                />
              </div>
            </div>

            {/* Bio */}
            <div>
              <label htmlFor="bio" className="block text-sm font-medium text-gray-700 mb-2">
                Bio
              </label>
              <textarea
                id="bio"
                name="bio"
                rows={4}
                value={formData.bio}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border bg-white border-gray-300  focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent"
                placeholder="Tell us about yourself..."
              />
            </div>

            {/* Social Links */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="twitter" className="block text-sm font-medium text-gray-700 mb-2">
                  Twitter Handle
                </label>
                <input
                  type="text"
                  id="twitter"
                  name="twitter"
                  value={formData.twitter}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border bg-white border-gray-300  focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent"
                  placeholder="@yourusername"
                />
              </div>

              <div>
                <label htmlFor="linkedin" className="block text-sm font-medium text-gray-700 mb-2">
                  LinkedIn Profile
                </label>
                <input
                  type="text"
                  id="linkedin"
                  name="linkedin"
                  value={formData.linkedin}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border bg-white border-gray-300  focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent"
                  placeholder="linkedin.com/in/yourusername"
                />
              </div>
            </div>

            {/* Writer Status */}
            <div className="border-t border-gray-300 pt-6">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_writer"
                  name="is_writer"
                  checked={formData.is_writer}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-brand-accent focus:ring-brand-accent border-gray-300 rounded"
                />
                <label htmlFor="is_writer" className="ml-2 block text-sm text-gray-700">
                  I want to become a writer and submit articles
                </label>
              </div>
              <p className="mt-1 text-sm text-gray-700">
                Check this box to request writer permissions. This will allow you to submit and publish articles.
              </p>
            </div>

            {/* Current Status */}
            <div className="bg-white  p-4 border border-gray-300">
              <h3 className="text-sm font-medium text-black mb-2">Current Status</h3>
              <div className="space-y-1">
                <p className="text-sm text-gray-700">
                  Writer Status: 
                  <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                    profile?.is_writer 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-gray-200 text-gray-700'
                  }`}>
                    {profile?.is_writer ? 'Active Writer' : 'Reader'}
                  </span>
                </p>
                {profile?.is_admin && (
                  <p className="text-sm text-gray-700">
                    Admin Status: 
                    <span className="ml-2 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                      Administrator
                    </span>
                  </p>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-6 border-t border-gray-300">
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-brand-accent text-white px-6 py-3  hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-brand-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Updating...' : 'Update Profile'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;